import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { toastify } from '../../../../../../utils/toast';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { analog_base_url } from "../../../../../../utils/urls";

const MONTH_OPTIONS = [
  { label: 'January', value: '01' }, { label: 'February', value: '02' },
  { label: 'March', value: '03' }, { label: 'April', value: '04' },
  { label: 'May', value: '05' }, { label: 'June', value: '06' },
  { label: 'July', value: '07' }, { label: 'August', value: '08' },
  { label: 'September', value: '09' }, { label: 'October', value: '10' },
  { label: 'November', value: '11' }, { label: 'December', value: '12' }
];

const getCurrentYearMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Last day of a YYYY-MM month as YYYY-MM-DD (readings are recorded as at month end).
const getMonthEndDate = (yearMonth) => {
  if (!/^\d{4}-\d{2}$/.test(yearMonth || '')) return '';
  const [year, month] = yearMonth.split('-').map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  return `${yearMonth}-${String(lastDay).padStart(2, '0')}`;
};

const formatMonthLabel = (yearMonth) => {
  if (!/^\d{4}-\d{2}$/.test(yearMonth || '')) return '';
  const [year, month] = yearMonth.split('-');
  const monthLabel = MONTH_OPTIONS.find((m) => m.value === month)?.label || month;
  return `${monthLabel} ${year}`;
};

// Pending readings are approved; failed ones are put back in the queue to be invoiced again.
const isSelectableStatus = (status) => status === 'pending' || status === 'failed';

// A reading is identified by meter AND month: the same meter has a row per month.
const rowKey = (item) => `${item.meterNumber}|${item.yearMonth}`;

const AnalogMeterHistoryTab = () => {
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);
  const [searchTerm, setSearchTerm] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(10);
  const [selectedMeters, setSelectedMeters] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  
  // Import Modal States
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importYearMonth, setImportYearMonth] = useState(getCurrentYearMonth());
  const [meters, setMeters] = useState([]);

  // Generate Invoices Modal States
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateYear, setGenerateYear] = useState(String(new Date().getFullYear()));
  const [generateMonth, setGenerateMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [generateNotify, setGenerateNotify] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Pagination options
  const rowsPerPageOptions = [10, 20, 50, 100];

  const statusOptions = [
    { label: 'All Statuses', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Reviewed', value: 'reviewed' },
    { label: 'Billed', value: 'billed' },
    { label: 'Failed', value: 'failed' }
  ];

  useEffect(() => {
    if (selectedFacilityId) {
      fetchHistoryData();
      fetchMeters();
    }
  }, [selectedFacilityId]);

  // Reset selections when data changes or filters change
  useEffect(() => {
    setSelectedMeters([]);
  }, [historyData, statusFilter, searchTerm]);

  const fetchHistoryData = async (month = selectedMonth) => {
    try {
      setLoading(true);
      const query = month ? `?yearMonth=${month}` : '';
      const response = await makeRequest2(
        `/api/app/analog-meters/get-history/${selectedFacilityId}${query}`,
        'GET'
      );
      if (response.success) {
        setHistoryData(response.data?.data || []);
        setMonths(response.data?.months || []);
        // The server answers with the month it returned (the latest when none was asked for).
        if (response.data?.yearMonth) setSelectedMonth(response.data.yearMonth);
      } else {
        toastify('Failed to fetch analog meter history', 'error');
      }
    } catch (error) {
      toastify('Error fetching analog meter history', 'error');
      setHistoryData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMeters = async () => {
    try {
      const response = await makeRequest2(
        `${analog_base_url}/${selectedFacilityId}`,
        'GET'
      );

      if (response.success) {
        const metersData = response.data?.meters || [];
        setMeters(metersData);
      }
    } catch (error) {
      console.error('Error loading meters', error);
    }
  };

  const handleMeterSelection = (key, isChecked) => {
    const reading = historyData.find(item => rowKey(item) === key);
    // Only pending or failed readings can be selected
    if (reading && !isSelectableStatus(reading.status)) return;

    if (isChecked) {
      setSelectedMeters(prev => [...prev, key]);
    } else {
      setSelectedMeters(prev => prev.filter(id => id !== key));
    }
  };

  const handleSelectAllChange = (e) => {
    const isChecked = e.checked;
    if (isChecked) {
      // Only select pending meters from filtered results
      const pendingKeys = filteredHistory
        .filter(item => isSelectableStatus(item.status))
        .map(rowKey);
      setSelectedMeters(pendingKeys);
    } else {
      setSelectedMeters([]);
    }
  };

  const handleCompleteReview = () => {
    const metersToUpdate = filteredHistory
      .filter(item => isSelectableStatus(item.status) && selectedMeters.includes(rowKey(item)))
      .map(item => ({ meterNumber: item.meterNumber, yearMonth: item.yearMonth }));

    if (metersToUpdate.length === 0) {
      toastify('No pending or failed readings selected', 'warning');
      return;
    }

    confirmDialog({
      message: `Are you sure you want to mark ${metersToUpdate.length} selected meter readings as reviewed? (Failed readings are put back in the queue so they are invoiced again.)`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          setLoading(true);
          const response = await makeRequest2(
            `/api/app/analog-meters/update-meters/${selectedFacilityId}`,
            'PUT',
            { items: metersToUpdate }
          );
          if (response.success) {
            toastify(response.message || 'Billing status updated successfully', 'success');
            fetchHistoryData();
            setSelectedMeters([]);
          } else {
            toastify(response.error || 'Failed to update billing status', 'error');
          }
        } catch (error) {
          toastify('Error updating billing status', 'error');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleStatusFilterChange = (e) => setStatusFilter(e.value);

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    fetchHistoryData(month);
  };

  // ----- IMPORT MODAL LOGIC -----
  const handleImportModalOpen = () => {
    setImportYearMonth(getCurrentYearMonth());
    setShowImportModal(true);
  };

  const handleImportModalClose = () => {
    setShowImportModal(false);
    setImportFile(null);
  };

  // Generate CSV Template
  // Only the meter serial number and the reading are needed: the month is chosen in the dialog.
  const generateCSVTemplate = () => {
    let csvContent = "serial_number,unit_name,meter_reading\n";

    meters.forEach(meter => {
      const unitName = meter.UnitInfo ? (meter.UnitInfo.name || meter.UnitInfo.unitNumber) : 'N/A';
      const meterNumber = meter.meterNumber || 'Unknown';
      csvContent += `${meterNumber},"${unitName}",\n`;
    });

    return csvContent;
  };

  const handleTemplateDownload = () => {
    const csvContent = generateCSVTemplate();
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `current_meter_readings_template.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setImportFile(e.target.files[0]);
    }
  };

  const handleImportSubmit = async (e) => {
    e.preventDefault();

    if (!importYearMonth) {
      toastify('Please select the year and month you are uploading readings for', 'error');
      return;
    }

    if (!importFile) {
      toastify('Please select a CSV file', 'error');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      // Append the month before the file so the server has it when the file arrives.
      formData.append('yearMonth', importYearMonth);
      formData.append('file', importFile);

      const response = await makeRequest2(
        `/api/app/analog-meters/import/${selectedFacilityId}`,
        'POST',
        formData
      );

      if (response.success) {
        const result = response.data || {};
        toastify(
          result.message || `Meter readings for ${formatMonthLabel(importYearMonth)} imported successfully`,
          result.partial ? 'warning' : 'success'
        );
        // Show the month that was just uploaded.
        fetchHistoryData(importYearMonth);
        setShowImportModal(false);
        setImportFile(null);
      } else {
        toastify(response.error || 'Failed to import meter readings', 'error');
      }
    } catch (error) {
      toastify('Error importing CSV', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ----- GENERATE INVOICES MODAL LOGIC -----
  const generateYearMonth = `${generateYear}-${generateMonth}`;
  const generateYearOptions = (() => {
    const thisYear = new Date().getFullYear();
    return [thisYear - 2, thisYear - 1, thisYear, thisYear + 1].map((y) => ({ label: String(y), value: String(y) }));
  })();

  // How many of the analog readings for the chosen month are ready to bill.
  const generateCounts = (Array.isArray(historyData) ? historyData : [])
    .filter((item) => item.yearMonth === generateYearMonth)
    .reduce(
      (counts, item) => ({ ...counts, [item.status]: (counts[item.status] || 0) + 1 }),
      { pending: 0, reviewed: 0, billed: 0, failed: 0 }
    );

  const handleGenerateModalOpen = () => {
    const now = new Date();
    setGenerateYear(String(now.getFullYear()));
    setGenerateMonth(String(now.getMonth() + 1).padStart(2, '0'));
    setGenerateNotify(false);
    setShowGenerateModal(true);
  };

  const handleGenerateSubmit = async (e) => {
    e.preventDefault();

    try {
      setGenerating(true);
      const response = await makeRequest2(
        `${analog_base_url}/trigger-billing/${selectedFacilityId}`,
        'POST',
        { yearMonth: generateYearMonth, sendNotifications: generateNotify },
        { timeout: 5 * 60 * 1000 } // billing a whole facility can take a while
      );

      if (response.success) {
        toastify(`Invoices generated for ${formatMonthLabel(generateYearMonth)}`, 'success');
        setShowGenerateModal(false);
        fetchHistoryData();
      } else {
        toastify(response.error || 'Failed to generate invoices', 'error');
      }
    } catch (error) {
      toastify('Error generating invoices', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const filteredHistory = Array.isArray(historyData)
    ? historyData.filter((item) => {
      const meterNumber = (item.meterNumber || '').toLowerCase();
      const yearMonth = (item.yearMonth || '').toLowerCase();
      const customerName = (item.customerInfo || '').toLowerCase();
      const textFilterPassed =
        meterNumber.includes(searchTerm.toLowerCase()) ||
        yearMonth.includes(searchTerm.toLowerCase()) ||
        customerName.includes(searchTerm.toLowerCase());
      const statusFilterPassed = statusFilter === 'all' || item.status === statusFilter;
      return textFilterPassed && statusFilterPassed;
    })
    : [];

  const pendingSelectedCount = filteredHistory.filter(
    item => isSelectableStatus(item.status) && selectedMeters.includes(rowKey(item))
  ).length;

  const pendingMetersCount = filteredHistory.filter(
    item => isSelectableStatus(item.status)
  ).length;

  const formatReading = (value) =>
    value !== null && value !== undefined ? `${Number(value).toFixed(2)} m³` : '0.00 m³';

  const avgUsageBodyTemplate = (rowData) => {
    const avgUsage = rowData.threeMonthAvgUsage || 0;
    const previousMonthsCount = rowData.previousMonthsCount || 0;
    const tooltip =
      previousMonthsCount > 0
        ? `Based on ${previousMonthsCount} previous month${previousMonthsCount > 1 ? 's' : ''}`
        : 'No previous data available';
    const currentUsage = parseFloat(rowData.totalUsage) || 0;
    const percentDiff = avgUsage > 0 ? ((currentUsage - avgUsage) / avgUsage) * 100 : 0;
    const isHighUsage = percentDiff > 20;
    const isLowUsage = percentDiff < -20;
    return (
      <div className="d-flex align-items-center" title={tooltip}>
        <span className={`me-2 ${isHighUsage ? 'text-danger' : isLowUsage ? 'text-success' : ''}`}>
          {formatReading(avgUsage)}
        </span>
        {previousMonthsCount === 0 && (
          <i className="ti ti-info-circle text-muted" style={{ fontSize: '0.75rem' }} />
        )}
        {previousMonthsCount > 0 && isHighUsage && (
          <i className="ti ti-arrow-up text-danger" style={{ fontSize: '0.75rem' }} />
        )}
        {previousMonthsCount > 0 && isLowUsage && (
          <i className="ti ti-arrow-down text-success" style={{ fontSize: '0.75rem' }} />
        )}
      </div>
    );
  };

  const statusBodyTemplate = (rowData) => {
    const statusColors = {
      pending: 'bg-warning text-light',
      reviewed: 'bg-info text-light',
      billed: 'bg-success text-light',
      failed: 'bg-danger text-light'
    };
    return (
      <div>
        <span className={`badge ${statusColors[rowData.status] || 'bg-secondary text-light'}`}>
          {rowData.status || 'unknown'}
        </span>
        {rowData.status === 'failed' && (
          <div
            className="small text-danger mt-1"
            style={{ maxWidth: '16rem', whiteSpace: 'normal' }}
            title={rowData.errorMessage || ''}
          >
            {rowData.errorMessage || 'No reason was recorded. Select it and use Approve Selected to try again.'}
          </div>
        )}
      </div>
    );
  };

  const totalUsageBodyTemplate = (rowData) => {
    const currentUsage = parseFloat(rowData.totalUsage) || 0;
    const avgUsage = parseFloat(rowData.threeMonthAvgUsage) || 0;
    const previousMonthsCount = rowData.previousMonthsCount || 0;
    if (previousMonthsCount === 0 || avgUsage === 0) return formatReading(currentUsage);
    const percentDiff = ((currentUsage - avgUsage) / avgUsage) * 100;
    const diffClass = percentDiff > 20 ? 'text-danger' : percentDiff < -20 ? 'text-success' : '';
    return (
      <div className="d-flex align-items-center">
        <span className={diffClass}>{formatReading(currentUsage)}</span>
        <small className="ms-2 text-muted">
          ({percentDiff > 0 ? '+' : ''}{percentDiff.toFixed(1)}%)
        </small>
      </div>
    );
  };

  const selectionTemplate = (rowData) => {
    const isSelectable = isSelectableStatus(rowData.status);
    const isSelected = selectedMeters.includes(rowKey(rowData));
    
    return (
      <div 
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '32px' }}
        title={!isSelectable ? 'Only pending or failed readings can be selected' : ''}
      >
        <Checkbox
          inputId={`checkbox-${rowKey(rowData)}`}
          name={`checkbox-${rowKey(rowData)}`}
          value={rowKey(rowData)}
          checked={isSelected}
          onChange={(e) => handleMeterSelection(rowKey(rowData), e.checked)}
          disabled={!isSelectable}
          className={`
            ${isSelected ? 'p-checkbox-checked' : ''} 
            ${!isSelectable ? 'p-checkbox-disabled' : ''}
          `}
          style={{
            transform: 'scale(1.2)',
          }}
        />
      </div>
    );
  };

  const headerCheckboxTemplate = () => {
    const pendingMeters = filteredHistory.filter(item => isSelectableStatus(item.status));
    const allPendingSelected =
      pendingMeters.length > 0 &&
      pendingMeters.every(item => selectedMeters.includes(rowKey(item)));
    const somePendingSelected =
      pendingMeters.some(item => selectedMeters.includes(rowKey(item)));

    return (
      <div className="d-flex justify-content-center align-items-center">
        <Checkbox
          inputId="header-checkbox"
          name="header-checkbox"
          checked={allPendingSelected}
          indeterminate={!allPendingSelected && somePendingSelected}
          onChange={handleSelectAllChange}
          disabled={pendingMeters.length === 0}
          className={`
            ${allPendingSelected ? 'p-checkbox-checked' : ''} 
            ${!allPendingSelected && somePendingSelected ? 'p-indeterminate' : ''}
            ${pendingMeters.length === 0 ? 'p-checkbox-disabled' : ''}
          `}
          style={{
            transform: 'scale(1.2)',
          }}
        />
      </div>
    );
  };

  if (!selectedFacilityId) {
    return (
      <div className="card">
        <div className="card-body text-center">
          Please select a facility to view meter history.
        </div>
      </div>
    );
  }

  return (
    <>
      <style jsx>{`
        .p-checkbox {
          position: relative !important;
          display: inline-flex !important;
          vertical-align: top !important;
          cursor: pointer !important;
        }
        
        .p-checkbox .p-checkbox-box {
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          border: 2px solid #dee2e6 !important;
          width: 20px !important;
          height: 20px !important;
          color: #495057 !important;
          border-radius: 4px !important;
          transition: background-color 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s !important;
          background: #ffffff !important;
        }
        
        .p-checkbox:not(.p-checkbox-disabled) .p-checkbox-box:hover {
          border-color: #007bff !important;
        }
        
        .p-checkbox.p-checkbox-checked .p-checkbox-box {
          background: #007bff !important;
          border-color: #007bff !important;
          color: #ffffff !important;
        }
        
        .p-checkbox.p-checkbox-checked .p-checkbox-box .p-checkbox-icon {
          color: #ffffff !important;
          font-size: 14px !important;
        }
        
        .p-checkbox.p-checkbox-disabled .p-checkbox-box {
          opacity: 0.6 !important;
          cursor: default !important;
        }
        
        .p-checkbox.p-indeterminate .p-checkbox-box {
          background: #007bff !important;
          border-color: #007bff !important;
          color: #ffffff !important;
        }
        
        .p-checkbox .p-checkbox-box .p-checkbox-icon {
          width: 12px !important;
          height: 12px !important;
          font-size: 12px !important;
          color: inherit !important;
        }
        
        .p-checkbox .p-checkbox-box input[type="checkbox"] {
          opacity: 0 !important;
          position: absolute !important;
          width: 100% !important;
          height: 100% !important;
          margin: 0 !important;
          cursor: pointer !important;
        }
      `}</style>

      <div className="card">
        <ConfirmDialog />
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
            <div className="d-flex col-md-7 col-xs-12 gap-2 mb-2 mb-md-0">
              <div className="col-md-6">
                <input
                  className="form-control"
                  placeholder="Search meter number, customer, year-month..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <Dropdown
                  value={statusFilter}
                  options={statusOptions}
                  onChange={handleStatusFilterChange}
                  className="w-100"
                  placeholder="Filter by Status"
                />
              </div>
              <div className="col-md-3">
                <select
                  className="form-select"
                  value={selectedMonth}
                  onChange={(e) => handleMonthChange(e.target.value)}
                  disabled={loading || months.length === 0}
                  title="Month shown"
                >
                  {months.length === 0 && <option value="">No months</option>}
                  {months.map((month) => (
                    <option key={month} value={month}>{formatMonthLabel(month)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-5 col-xs-12 d-flex justify-content-end align-items-center gap-2">
              <button
                className="btn btn-primary"
                onClick={handleImportModalOpen}
              >
                Import Readings
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={handleGenerateModalOpen}
              >
                Generate Invoices
              </button>
              <span className="me-2">
                Pending: {pendingSelectedCount} of {pendingMetersCount}
              </span>
              <button
                className="btn btn-success"
                onClick={handleCompleteReview}
                disabled={pendingSelectedCount === 0}
              >
                Approve Selected
              </button>
            </div>
          </div>
          <div className="dt-responsive table-responsive">
            <DataTable
              value={filteredHistory}
              emptyMessage="No meter readings found."
              sortMode="multiple"
              paginator
              rows={rows}
              rowsPerPageOptions={rowsPerPageOptions}
              stripedRows
              loading={loading}
              tableStyle={{ minWidth: '50rem' }}
              stateStorage="session"
              stateKey="analog-meter-history-state"
            >
              <Column 
                header={headerCheckboxTemplate} 
                body={selectionTemplate} 
                style={{ width: '4rem', textAlign: 'center' }}
                headerStyle={{ textAlign: 'center' }}
              />
              <Column field="meterNumber" header="Meter Number" sortable />
              <Column field="customerInfo" header="Customer" sortable />
              <Column field="unitInfo" header="Unit" sortable />
              <Column
                field="previousReading"
                header="Previous Reading"
                body={(rowData) => formatReading(rowData.previousReading)}
                sortable
              />
              <Column
                field="currentReading"
                header="Current Reading"
                body={(rowData) => formatReading(rowData.currentReading)}
                sortable
              />
              <Column field="totalUsage" header="Total Usage" body={totalUsageBodyTemplate} sortable />
              <Column
                field="threeMonthAvgUsage"
                header="3-Month Average"
                body={avgUsageBodyTemplate}
                sortable
                style={{ backgroundColor: '#f8f9fa' }}
              />
              <Column field="yearMonth" header="Year/Month" sortable />
              <Column field="status" header="Status" body={statusBodyTemplate} sortable />
            </DataTable>
          </div>
        </div>

        {/* Import Meter Readings Modal */}
        {showImportModal && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Import Current Meter Readings</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleImportModalClose}
                  ></button>
                </div>
                <div className="modal-body">
                  <p>
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      onClick={handleTemplateDownload}
                    >
                      Download facility meters template (.csv)
                    </button>
                  </p>
                  <div className="alert alert-info">
                    <strong>Template Info</strong>
                    <ul className="mb-0">
                      <li>The CSV lists all the facility's meters by <strong>serial_number</strong></li>
                      <li>Only fill in the empty <strong>meter_reading</strong> column; meters left blank are skipped</li>
                      <li>Choose the month below: readings are recorded as at the end of that month</li>
                    </ul>
                  </div>

                  <form onSubmit={handleImportSubmit}>
                    <div className="mb-3">
                      <label className="form-label">
                        Year and month you are uploading for<span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="month"
                        className="form-control"
                        value={importYearMonth}
                        onChange={(e) => setImportYearMonth(e.target.value)}
                        required
                      />
                      {importYearMonth && (
                        <small className="text-muted">
                          Readings will be dated {getMonthEndDate(importYearMonth)} (end of {formatMonthLabel(importYearMonth)})
                        </small>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Upload Completed CSV File</label>
                      <input
                        type="file"
                        accept=".csv"
                        className="form-control"
                        onChange={handleFileChange}
                      />
                    </div>
                    <div className="text-end">
                      <button
                        type="button"
                        className="btn btn-outline-secondary me-2"
                        onClick={handleImportModalClose}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading || !importFile || !importYearMonth}
                      >
                        {loading ? (
                          <span className="spinner-border spinner-border-sm me-1"></span>
                        ) : null}
                        Submit
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Generate Invoices Modal */}
        {showGenerateModal && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Generate Invoices</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowGenerateModal(false)}
                    disabled={generating}
                  ></button>
                </div>
                <form onSubmit={handleGenerateSubmit}>
                  <div className="modal-body">
                    <p className="text-muted">
                      Choose the year and month to bill. An invoice is created for every{' '}
                      <strong>reviewed</strong> meter reading of that month.
                    </p>
                    <div className="row mb-3">
                      <div className="col-6">
                        <label className="form-label">Year</label>
                        <select
                          className="form-select"
                          value={generateYear}
                          onChange={(e) => setGenerateYear(e.target.value)}
                          disabled={generating}
                        >
                          {generateYearOptions.map((year) => (
                            <option key={year.value} value={year.value}>{year.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="form-label">Month</label>
                        <select
                          className="form-select"
                          value={generateMonth}
                          onChange={(e) => setGenerateMonth(e.target.value)}
                          disabled={generating}
                        >
                          {MONTH_OPTIONS.map((month) => (
                            <option key={month.value} value={month.value}>{month.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="alert alert-light border">
                      <strong>{formatMonthLabel(generateYearMonth)}</strong>
                      <div>Ready to bill (reviewed): <strong>{generateCounts.reviewed}</strong></div>
                      <div>Waiting for approval (pending): <strong>{generateCounts.pending}</strong></div>
                      <div>Already billed: <strong>{generateCounts.billed}</strong></div>
                      {generateCounts.failed > 0 && (
                        <div className="text-danger">Failed: <strong>{generateCounts.failed}</strong></div>
                      )}
                    </div>
                    {generateCounts.failed > 0 && (
                      <div className="alert alert-danger">
                        {generateCounts.failed} reading(s) for this month failed to invoice. The reason is shown on each row
                        in the table. After fixing it, select them and click <em>Approve Selected</em> to try again.
                      </div>
                    )}
                    {generateCounts.pending > 0 && (
                      <div className="alert alert-warning">
                        {generateCounts.pending} reading(s) for this month are still pending. Approve them first
                        (select them and click <em>Approve Selected</em>) or they will not be invoiced.
                      </div>
                    )}
                    {generateCounts.reviewed === 0 && (
                      <div className="alert alert-warning">
                        There are no reviewed readings for {formatMonthLabel(generateYearMonth)} to invoice.
                      </div>
                    )}

                    <div className="form-check">
                      <input
                        id="generate-notify"
                        type="checkbox"
                        className="form-check-input"
                        checked={generateNotify}
                        onChange={(e) => setGenerateNotify(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="generate-notify">
                        Send invoice notifications to customers
                      </label>
                    </div>
                    <small className="text-muted d-block mt-2">
                      Smart meters in this facility are also billed for the selected month.
                    </small>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowGenerateModal(false)}
                      disabled={generating}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={generating || generateCounts.reviewed === 0}
                    >
                      {generating ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                      Generate Invoices
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AnalogMeterHistoryTab;